import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CompanyManagementService } from '@/services/company/companyManagementService';
import { Company, CreateCompanyRequest, UpdateCompanyRequest } from '@/types/company';
import { ApiError } from '@/types/carAuctionClient';

export const useCompanyManagement = (
  id?: string,
  page: number = 1,
  limit: number = 15,
  searchQuery?: string,
  statuses?: string[],
) => {
  const queryClient = useQueryClient();

  // Get all companies with pagination and search (admin can see all data)
  const companiesQuery = useQuery<{
    companies: Company[];
    totalCount: number;
    totalPages: number;
    currentPage: number;
  }, ApiError>({
    queryKey: ['companyManagement', page, limit, searchQuery, statuses],
    queryFn: () => CompanyManagementService.getAllCompanies(page, limit, searchQuery, statuses),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Get company by ID
  const companyByIdQuery = useQuery<Company, ApiError>({
    queryKey: ['companyManagement', id],
    queryFn: () => CompanyManagementService.getCompanyById(id as string),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Create company mutation
  const createCompanyMutation = useMutation({
    mutationFn: (data: CreateCompanyRequest) => CompanyManagementService.createCompany(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyManagement'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] }); // Also invalidate public companies
    },
  });

  // Update company mutation
  const updateCompanyMutation = useMutation({
    mutationFn: (data: UpdateCompanyRequest) => CompanyManagementService.updateCompany(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['companyManagement'] });
      queryClient.invalidateQueries({ queryKey: ['companyManagement', variables._id] });
      queryClient.invalidateQueries({ queryKey: ['companies'] }); // Also invalidate public companies
    },
  });

  const updateCompanyStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      CompanyManagementService.updateCompanyStatus(id, status),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['companyManagement'] });
      queryClient.invalidateQueries({ queryKey: ['companyManagement', data._id] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });

  // Delete company mutation
  const deleteCompanyMutation = useMutation({
    mutationFn: (id: string) => CompanyManagementService.deleteCompany(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companyManagement'] });
      queryClient.invalidateQueries({ queryKey: ['companies'] }); // Also invalidate public companies
    },
  });

  // Get company members query
  const invalidateCompany = (companyId: string) => {
    queryClient.invalidateQueries({ queryKey: ['companyManagement', companyId] });
  };

  const addCommentMutation = useMutation({
    mutationFn: ({ companyId, text }: { companyId: string; text: string }) =>
      CompanyManagementService.addComment(companyId, text),
    onSuccess: (data) => invalidateCompany(data._id),
  });

  const updateCommentMutation = useMutation({
    mutationFn: ({
      companyId,
      commentId,
      text,
    }: {
      companyId: string;
      commentId: string;
      text: string;
    }) => CompanyManagementService.updateComment(companyId, commentId, text),
    onSuccess: (data) => invalidateCompany(data._id),
  });

  const deleteCommentMutation = useMutation({
    mutationFn: ({
      companyId,
      commentId,
    }: {
      companyId: string;
      commentId: string;
    }) => CompanyManagementService.deleteComment(companyId, commentId),
    onSuccess: (data) => invalidateCompany(data._id),
  });

  const companyMembersQuery = useQuery<{
    members: Array<{
      _id: string;
      username: string;
      email: string;
      role: 'individual' | 'dealer' | 'business' | 'admin';
      firstName: string;
      lastName: string;
      phoneNumber: string;
      bankIdVerified: boolean;
      isActive: boolean;
      lastLogin: string;
      createdAt: string;
      updatedAt: string;
      address: {
        street: string;
        city: string;
        state: string;
        zipCode: string;
        country: string;
      };
      profilePhoto: {
        url: string;
        publicId: string;
      };
      company?: string;
    }>;
    totalCount: number;
  }, ApiError>({
    queryKey: ['companyManagement-members', id],
    queryFn: () => CompanyManagementService.getMembersOfCompany(id as string),
    enabled: Boolean(id),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    // Data
    companies: companiesQuery.data?.companies || [],
    company: companyByIdQuery.data,
    companyMembers: companyMembersQuery.data?.members || [],
    
    // Pagination data
    totalCount: companiesQuery.data?.totalCount || 0,
    totalPages: companiesQuery.data?.totalPages || 0,
    currentPage: companiesQuery.data?.currentPage || 1,
    
    // Loading states
    isLoadingCompanies: companiesQuery.isLoading,
    isLoadingCompany: companyByIdQuery.isLoading,
    isLoadingCompanyMembers: companyMembersQuery.isLoading,
    
    // Error states
    companiesError: companiesQuery.error,
    companyError: companyByIdQuery.error,
    companyMembersError: companyMembersQuery.error,
    
    // Refetch functions
    refetchCompanies: companiesQuery.refetch,
    refetchCompany: companyByIdQuery.refetch,
    refetchCompanyMembers: companyMembersQuery.refetch,
    
    // Create company
    createCompany: createCompanyMutation.mutate,
    createCompanyAsync: createCompanyMutation.mutateAsync,
    isCreatingCompany: createCompanyMutation.isPending,
    createCompanyError: createCompanyMutation.error,
    createCompanySuccess: createCompanyMutation.isSuccess,
    resetCreateCompany: createCompanyMutation.reset,
    
    // Update company
    updateCompany: updateCompanyMutation.mutate,
    updateCompanyAsync: updateCompanyMutation.mutateAsync,
    isUpdatingCompany: updateCompanyMutation.isPending,
    updateCompanyError: updateCompanyMutation.error,
    updateCompanySuccess: updateCompanyMutation.isSuccess,
    resetUpdateCompany: updateCompanyMutation.reset,

    updateCompanyStatusAsync: updateCompanyStatusMutation.mutateAsync,
    isUpdatingCompanyStatus: updateCompanyStatusMutation.isPending,
    
    // Delete company
    deleteCompany: deleteCompanyMutation.mutate,
    deleteCompanyAsync: deleteCompanyMutation.mutateAsync,
    isDeletingCompany: deleteCompanyMutation.isPending,
    deleteCompanyError: deleteCompanyMutation.error,
    deleteCompanySuccess: deleteCompanyMutation.isSuccess,
    resetDeleteCompany: deleteCompanyMutation.reset,

    addCommentAsync: addCommentMutation.mutateAsync,
    isAddingComment: addCommentMutation.isPending,
    updateCommentAsync: updateCommentMutation.mutateAsync,
    isUpdatingComment: updateCommentMutation.isPending,
    deleteCommentAsync: deleteCommentMutation.mutateAsync,
    isDeletingComment: deleteCommentMutation.isPending,
    
    // Debug info
    debug: {
      companies: companiesQuery.data,
      company: companyByIdQuery.data,
      isLoadingCompanies: companiesQuery.isLoading,
      isLoadingCompany: companyByIdQuery.isLoading,
      companiesError: companiesQuery.error,
      companyError: companyByIdQuery.error,
    },
  };
};
